import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_AiRetake = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/ai-retake',
			name: 'AI Retake — Perfect Every Portrait in One Tap',
			description:
				'Upload a portrait and let AI Retake open closed eyes, smooth skin, and sharpen photo quality in one pass — no edits to your look. Try Lift now.',
			url: 'https://liftapp.ai/ai-retake',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/ai-retake#product' },
				{ '@id': 'https://liftapp.ai/ai-retake#howto' },
				{ '@id': 'https://liftapp.ai/ai-retake#benefits' },
				{ '@id': 'https://liftapp.ai/ai-retake#faq' },
				{ '@id': 'https://liftapp.ai/ai-retake#why-lift' },
				{ '@id': 'https://liftapp.ai/ai-retake#explore-tools' },
				{ '@id': 'https://liftapp.ai/ai-retake#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/ai-retake#product',
			name: 'AI Retake: Perfect Your Portrait in One Tap',
			description:
				'Upload a portrait and tap Retake — Lift opens closed eyes, clears skin, and sharpens photo quality while keeping your exact look. Try Lift now.',
			url: 'https://liftapp.ai/ai-retake',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/ai-retake#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload Your Portrait',
					text: "Choose a photo from your device or take one using Lift's app.",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Tap Retake To Start',
					text: 'One tap starts the full process, with no separate settings or sliders to configure first.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'AI Analyzes And Perfects',
					text: 'Lift scans your face, opens closed eyes, clears skin, and sharpens photo quality in one pass.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Compare And Download',
					text: 'Slide between before and after, then download your finished portrait or generate it again.',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-retake#benefits',
			name: 'Who can benefit from AI Retake?',
			description:
				"Lift's AI Retake is useful for daters, families, creators, and job seekers who want a polished portrait without changing how they look.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Online daters — A polished profile photo makes a stronger first impression without looking staged. AI Retake opens closed eyes and clears skin while keeping your exact face and background.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'LinkedIn and job seekers — A natural, professional-looking photo helps you stand out on a resume or profile. Retake sharpens photo quality and evens out skin tone in a single pass.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Content creators and influencers — Consistently polished portraits keep a feed looking professional without extra editing apps. Retake handles eyes, skin, and sharpness together every time you upload a new shot.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: "Families preserving candid photos — A great family moment shouldn't be ruined by a blink or a stray blemish. Retake fixes the flaw while keeping the pose, background, and moment untouched.",
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Everyday photo takers — Not every phone photo comes out flattering, even when the moment is worth keeping. Retake cleans up skin and lighting issues without reshooting the scene.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl: 'https://liftapp.ai/_next/static/media/benefits.webp',
				name: 'AI Retake by Lift',
				description:
					"Example of how Lift AI Retake perfects a portrait by opening closed eyes and clearing skin while keeping the subject's exact look.",
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/ai-retake#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is the Lift AI Retake?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "AI Retake is Lift's image-to-image tool that perfects a single portrait in one pass. It opens closed eyes, clears skin, and improves photo quality while keeping your face, pose, and background exactly the same.",
					},
				},
				{
					'@type': 'Question',
					name: 'How does the Lift AI Retake work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload a portrait and tap Retake to start the process. Lift analyzes your photo, then opens your eyes, cleans up skin, and sharpens quality before showing a before-and-after comparison.',
					},
				},
				{
					'@type': 'Question',
					name: 'Will AI Retake change how I look?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'No — AI Retake only fixes temporary issues like closed eyes, blemishes, and soft focus. Your identity, pose, outfit, and background stay exactly as they were in the original photo.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe to upload my photos to Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — Lift processes your photos securely and uses them only to generate your results.',
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-retake#why-lift',
			name: "Why Lift's AI Retake stands out",
			description:
				"Lift's AI Retake combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl: 'https://liftapp.ai/_next/static/media/whyUse.webp',
				name: 'Why use Lift AI Retake',
				description:
					'Comparison of Lift AI Retake benefits — professional quality, intelligent rendering, multi-platform access, and cost savings.',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-retake#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/ai-retake#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Retake',
					item: 'https://liftapp.ai/ai-retake',
				},
			],
		},
	],
};
